/**
 * axios setup to use mock service
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { enqueueSnackbar } from 'notistack';
import { getClerkToken } from 'utils/auth/token';
import { redirectToClerkSignInFromHttp } from 'utils/auth/clerkAuthActions';
import { isAppMeBootstrapDone, isAppUserInOnboardingFlow } from 'utils/auth/sessionBootstrapFlags';
import {
  dispatchGlobalRequestFailure,
  dispatchGlobalRequestStart,
  dispatchGlobalRequestSuccess,
} from 'utils/globalRequestStatusStore';
/** Default message when the server returns no body (shown to callers after interceptor reject). */
export const DEFAULT_AXIOS_FRIENDLY_ERROR_MESSAGE =
  'נראה שיש בעיית חיבור לאינטרנט, אנא בדקו את הרשת';

const MISSING_COMPANY_CONTEXT_MSG =
  'חסר הקשר ארגון. התחבר מחדש כדי לסנכרן את החשבון.';

function urlAllowsMissingCompanyContext(url: string | undefined): boolean {
  if (!url) return false;
  const path = url.split('?')[0];
  if (path.includes('/api/users/me')) return true;
  if (path.includes('/api/invitation/')) return true;
  if (path.includes('/api/native-auth/')) return true; // דפדפן המערכת של האפליקציה — אין companyId ב-localStorage
  return false;
}

function pathnameAllowsMissingCompanyContext(): boolean {
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname;
  return (
    p.includes('/company-onboarding') ||
    p.includes('/accept-invite') ||
    p === '/login' ||
    p === '/register' ||
    p === '/native-login'
  );
}

function shouldBlockRequestWithoutCompany(
  config: InternalAxiosRequestConfig & { skipCompanyContextCheck?: boolean },
  hasToken: boolean,
): boolean {
  if (!hasToken) return false;
  if (config.skipCompanyContextCheck) return false;
  if (urlAllowsMissingCompanyContext(config.url)) return false;
  if (pathnameAllowsMissingCompanyContext()) return false;
  if (!isAppMeBootstrapDone()) return false;
  if (isAppUserInOnboardingFlow()) return false;
  if (localStorage.getItem('companyId')) return false;
  return true;
}

const ERROR_REPORT_WEBHOOK_URL = 'https://hook.eu1.make.com/bnn26lt2nsh7lxg9jhvnuvwfv69jxyuf';
const WEBHOOK_THROTTLE_MS = 30_000;
const lastWebhookSentAtByKey = new Map<string, number>();

function serializeErrorBodyForWebhook(data: unknown): string {
  if (data === undefined || data === null) return '';
  if (typeof data === 'string') return data;
  try {
    return JSON.stringify(data);
  } catch {
    return String(data);
  }
}

function buildFullRequestUrl(config: InternalAxiosRequestConfig | undefined): string {
  if (!config) return '';
  const base = (config.baseURL || '').replace(/\/$/, '');
  const path = config.url || '';
  if (/^https?:\/\//i.test(path)) return path;
  const p = path.startsWith('/') ? path : path ? `/${path}` : '';
  if (!base) {
    if (typeof window !== 'undefined' && p) return `${window.location.origin}${p}`;
    return p;
  }
  try {
    return new URL(p || '/', base.endsWith('/') ? base : `${base}/`).href;
  } catch {
    return `${base}${p}`;
  }
}

function reportFailedRequestToWebhook(error: unknown): void {
  if (!axios.isAxiosError(error)) return;

  const status = error.response?.status;
  if (status !== undefined && status >= 200 && status < 300) return;

  const config = error.config;
  const method = (config?.method || 'get').toUpperCase();
  const url = buildFullRequestUrl(config);
  if (!url || url.startsWith(ERROR_REPORT_WEBHOOK_URL)) return;

  const effectiveStatus = status ?? 0;
  const errorMessage = error.response
    ? serializeErrorBodyForWebhook(error.response.data)
    : error.message || 'Network error';

  const throttleKey = `${method}:${url}:${effectiveStatus}:${errorMessage.slice(0, 500)}`;
  const now = Date.now();
  const last = lastWebhookSentAtByKey.get(throttleKey);
  if (last !== undefined && now - last < WEBHOOK_THROTTLE_MS) return;
  lastWebhookSentAtByKey.set(throttleKey, now);

  const companyId = localStorage.getItem('companyId') ?? '';

  const payload = {
    url,
    method,
    status: effectiveStatus,
    errorMessage,
    companyId,
    timestamp: new Date().toISOString(),
  };

  void fetch(ERROR_REPORT_WEBHOOK_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch(() => undefined);
}

declare module 'axios' {
  export interface AxiosRequestConfig {
    skipServerContext?: boolean;
    /** When true, request does not affect the header global status indicator */
    skipGlobalLoading?: boolean;
    /**
     * Skip guard that blocks API calls when `companyId` is missing (after initial `/me` sync).
     * Use for rare bootstrap / public endpoints that must run without org context.
     */
    skipCompanyContextCheck?: boolean;
  }
}

type fetchTypes = 'app' | 'history' | 'messages' | 'api' | 'priority' | 'newapi' | 'newapp' | 'servernode' | 'dashboard';

export type FetchMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';

/**
 * In dev, use same-origin `/api` via Vite proxy (avoids CORS) — **on by default**.
 * Set `VITE_USE_APP_PROXY=false` to use full URL + `applyServerContext()` (may hit CORS from localhost).
 * Set `VITE_PROXY_APP_TARGET` (e.g. https://app3.funner.co.il) so the proxy hits the right app host.
 * In dev, prefer same-origin `/api` (proxy) — do not read `VITE_APP_APP_API_URL` first or the browser will call app3 directly (CORS).
 */
export function isDevViteProxyEnabled(): boolean {
  return import.meta.env.DEV && import.meta.env.VITE_USE_APP_PROXY !== 'false';
}

const defaultAppApiBaseUrl = (): string => {
  if (isDevViteProxyEnabled()) {
    return '';
  }
  if (import.meta.env.VITE_APP_APP_API_URL) {
    return import.meta.env.VITE_APP_APP_API_URL;
  }
  return 'https://app.funner.co.il';
};

/** In dev with Vite proxy, use relative `/api` so newapi/history/messages avoid CORS (same as `app`). */
function devProxyOrEnv(envOverride: string | undefined, fallback: string): string {
  if (isDevViteProxyEnabled()) {
    return '';
  }
  return envOverride || fallback;
}

/** Always a full origin — `axiosApiServices` targets the env DB host even when other services use the Vite proxy. */
function apiServicesFullBaseUrl(): string {
  return import.meta.env.VITE_APP_API_URL || 'https://api.funner.co.il';
}

/** Full backend origin from env — never routed through the Vite `/api` app proxy. */
function envFullBaseUrl(envOverride: string | undefined, fallback: string): string {
  return envOverride || fallback;
}

/** לוחות בקרה: ב-dev תמיד `/api/dashboard` דרך Vite (גם כש-VITE_USE_APP_PROXY=false); בפרוד → servernode מ-env. */
function dashboardBaseUrl(): string {
  if (import.meta.env.DEV) {
    return '';
  }
  return envFullBaseUrl(import.meta.env.VITE_APP_SERVERNODE_API_URL, 'http://localhost:3000');
}

const API_BASE_URLS: { [key in fetchTypes]: string } = {
  app: defaultAppApiBaseUrl(),
  history: envFullBaseUrl(import.meta.env.VITE_APP_HISTORY_API_URL, 'https://app.funner.co.il'),
  messages: devProxyOrEnv(import.meta.env.VITE_APP_MESSAGES_API_URL, 'https://app.funner.co.il'),
  api: apiServicesFullBaseUrl(),
  priority: envFullBaseUrl(import.meta.env.VITE_APP_PRIORITY_API_URL, 'https://api.funner.co.il'),
  newapi: envFullBaseUrl(import.meta.env.VITE_APP_NEWAPI_API_URL, 'https://newapi.funner.co.il'),
  newapp: envFullBaseUrl(import.meta.env.VITE_APP_NEWAPP_API_URL, 'https://newapp.funner.co.il'),
  servernode: envFullBaseUrl(import.meta.env.VITE_APP_SERVERNODE_API_URL, 'http://localhost:3000'),
  dashboard: dashboardBaseUrl(),
};

const RE_SUBDOMAIN = /^(https?:\/\/)([^.]+)(\..+)$/;

/**
 * Convert a PascalCase key to camelCase, matching Newtonsoft.Json CamelCaseNamingStrategy:
 *   "Id"            → "id"
 *   "GPTToken"      → "gptToken"
 *   "CompanyStatus" → "companyStatus"
 */
function pascalToCamelCase(str: string): string {
  if (!str || str[0] === str[0].toLowerCase()) return str;

  let i = 0;
  while (i < str.length && str[i] === str[i].toUpperCase() && str[i] !== str[i].toLowerCase()) {
    i++;
  }

  if (i <= 1) return str[0].toLowerCase() + str.slice(1);
  if (i >= str.length) return str.toLowerCase();
  return str.slice(0, i - 1).toLowerCase() + str.slice(i - 1);
}

/** מנורמל תגובות שרת עם PascalCase (למשל מסלולי `app`) — לשימוש ב-fetcher-ים ללא interceptor */
export function toCamelCaseKeys(data: unknown): unknown {
  if (data === null || data === undefined || typeof data !== 'object') return data;
  if (Array.isArray(data)) return data.map(toCamelCaseKeys);
  return Object.entries(data as Record<string, unknown>).reduce<Record<string, unknown>>((acc, [key, value]) => {
    acc[pascalToCamelCase(key)] = toCamelCaseKeys(value);
    return acc;
  }, {});
}

/**
 * If localStorage contains a serverContext, it will be added to the baseUrl.
 *   https://app.funner.co.il → https://app2.funner.co.il
 */
export function applyServerContext(baseUrl: string): string {
  // Skip server context for IP addresses (like http://109.207.79.184:3000)
  if (/^https?:\/\/\d+\.\d+\.\d+\.\d+/.test(baseUrl)) {
    return baseUrl;
  }

  const raw = localStorage.getItem('serverContext') ?? '0';
  const ctx = Number(raw);
  if (!ctx) return baseUrl;

  const m = baseUrl.match(RE_SUBDOMAIN);
  if (!m) return baseUrl;

  const [, protocol, sub, rest] = m;
  return `${protocol}${sub}${ctx}${rest}`;
}

// ==============================|| FOR APP SERVICES ||============================== //

axios.defaults.headers.common['X-Server-Origin'] = import.meta.env.VITE_APP_SERVER_ORIGIN || 'server_1';

// --- small token cache to avoid repeated getToken() calls in bursts ---
//* ===== FIX (2026-07-19) — חלק מתיקון "רענון אקראי" =====
//* ה-TTL קוצר מ-30s ל-10s: טוקן של Clerk JWT template חי בדרך כלל 60 שניות בלבד.
//* cache מקומי של 30s מעל ה-cache הפנימי של Clerk יצר חלון שבו בקשות יצאו עם טוקן
//* שכבר פג בצד השרת → 401 → redirect ל-Clerk (שנראה למשתמש כרענון אקראי של המערכת).
//* 10s עדיין בולם "פרצי" בקשות (המטרה המקורית של ה-cache) אך מצמצם מאוד את חלון הפקיעה.
let cachedToken: string | null = null;
let cachedAt = 0;
const TOKEN_TTL_MS = 10_000;

async function getAuthToken(): Promise<string | null> {
  const now = Date.now();
  if (cachedToken && now - cachedAt < TOKEN_TTL_MS) return cachedToken;

  const token = await getClerkToken();
  cachedToken = token;
  cachedAt = now;
  return token;
}

type CreateAxiosOptions = { preserveAxiosErrors?: boolean };

const createAxiosInstance = (baseURL: string, options?: CreateAxiosOptions): AxiosInstance => {
  const instance = axios.create({ baseURL });

  instance.interceptors.request.use(
    async (config) => {
      // Add server context to baseURL if needed
      if (config.skipServerContext) {
        config.baseURL = baseURL;
      } else {
        config.baseURL = applyServerContext(baseURL);
      }

      // ✅ Clerk JWT token
      const token = await getAuthToken();
      if (token) {
        config.headers = config.headers ?? {};
        config.headers['Authorization'] = `Bearer ${token}`;
      } else {
        // No token → ensure header not stale
        if (config.headers && 'Authorization' in config.headers) {
          // @ts-ignore
          delete config.headers.Authorization;
        }
      }

      const companyId = localStorage.getItem('companyId');
      if (companyId) {
        config.headers = config.headers ?? {};
        config.headers['X-Company-Id'] = companyId;
      } else if (config.headers && 'X-Company-Id' in config.headers) {
        // @ts-ignore
        delete config.headers['X-Company-Id'];
      }

      const cfg = config as InternalAxiosRequestConfig & { skipCompanyContextCheck?: boolean };
      if (shouldBlockRequestWithoutCompany(cfg, Boolean(token))) {
        enqueueSnackbar(MISSING_COMPANY_CONTEXT_MSG, { variant: 'warning' });
        return Promise.reject(
          new axios.AxiosError(
            MISSING_COMPANY_CONTEXT_MSG,
            'ERR_NETWORK',
            cfg,
            undefined,
            undefined,
          ),
        );
      }

      return config;
    },
    (error) => Promise.reject(error),
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      reportFailedRequestToWebhook(error);

      //* ===== FIX (2026-07-19) — 401 לא גורר יותר רענון מיידי של כל המערכת =====
      //* התנהגות ישנה: כל 401, מכל אחד מהשרתים, הפעיל מיד redirectToSignIn — ניווט
      //* דפדפן מלא ל-Clerk וחזרה. כשהסשן עדיין תקף (401 רגעי בגלל טוקן שפג בדיוק,
      //* deploy/restart של backend וכו') המשתמש חווה "רענון אקראי" של האפליקציה.
      //*
      //* התנהגות חדשה (שלושה שלבים):
      //*   1. ניקוי ה-cache המקומי + הנפקת טוקן טרי מ-Clerk (skipCache=true).
      //*   2. ניסיון חוזר של הבקשה פעם אחת בלבד (_authRetried מונע לולאה; retry על
      //*      401 בטוח גם ל-POST — השרת דחה את הבקשה לפני שעיבד אותה).
      //*   3. רק אם גם הניסיון החוזר נכשל ב-401 (או שאין טוקן בכלל) →
      //*      redirectToClerkSignInFromHttp, שבתורו מוותר על ה-redirect אם Clerk
      //*      עדיין מדווח על סשן פעיל (ראו utils/auth/clerkAuthActions.ts).
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        cachedToken = null;
        cachedAt = 0;

        const cfg = error.config as
          | (InternalAxiosRequestConfig & { _authRetried?: boolean })
          | undefined;

        if (cfg && !cfg._authRetried) {
          cfg._authRetried = true;
          const freshToken = await getClerkToken({ skipCache: true }).catch(() => null);
          if (freshToken) {
            //* מזריעים את ה-cache כדי שה-request interceptor של הניסיון החוזר
            //* ישתמש בטוקן הטרי. אם הניסיון החוזר נכשל, הדחייה שלו כבר עוברת
            //* דרך ה-interceptor הזה שוב (עם _authRetried=true) ומטופלת שם.
            cachedToken = freshToken;
            cachedAt = Date.now();
            return instance.request(cfg);
          }
        }

        void redirectToClerkSignInFromHttp();
      }

      if (options?.preserveAxiosErrors) {
        return Promise.reject(error);
      }
      if (axios.isAxiosError(error)) {
        return Promise.reject(error.response?.data ?? DEFAULT_AXIOS_FRIENDLY_ERROR_MESSAGE);
      }
      return Promise.reject(error);
    },
  );

  instance.interceptors.request.use(
    (config) => {
      const cfg = config as InternalAxiosRequestConfig & { skipGlobalLoading?: boolean };
      if (!cfg.skipGlobalLoading) {
        dispatchGlobalRequestStart();
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  instance.interceptors.response.use(
    (response) => {
      const cfg = response.config as InternalAxiosRequestConfig & { skipGlobalLoading?: boolean };
      if (!cfg.skipGlobalLoading) {
        dispatchGlobalRequestSuccess();
      }
      return response;
    },
    (error: unknown) => {
      const cfg = axios.isAxiosError(error) ? error.config : undefined;
      const skip = (cfg as InternalAxiosRequestConfig & { skipGlobalLoading?: boolean })?.skipGlobalLoading;
      if (!skip) {
        dispatchGlobalRequestFailure();
      }
      return Promise.reject(error);
    },
  );

  return instance;
};

export const axiosAppServices = createAxiosInstance(API_BASE_URLS.app);
/** Same as `axiosAppServices` but failed requests reject with full AxiosError (HTTP status for UX). */
export const axiosAppServicesWithStatus = createAxiosInstance(API_BASE_URLS.app, {
  preserveAxiosErrors: true,
});
export const axiosHistoryServices = createAxiosInstance(API_BASE_URLS.history);
/** Same auth/headers as history API, but failed requests reject with full AxiosError (status code for UX). */
export const axiosHistoryServicesWithStatus = createAxiosInstance(API_BASE_URLS.history, {
  preserveAxiosErrors: true,
});
export const axiosMessagesServices = createAxiosInstance(API_BASE_URLS.messages);
export const axiosApiServices = createAxiosInstance(API_BASE_URLS.api);
export const axiosPriorityServices = createAxiosInstance(API_BASE_URLS.priority);
/** Same auth/headers as priority API, but failed requests reject with full AxiosError (status for Meta health UX). */
export const axiosPriorityServicesWithStatus = createAxiosInstance(API_BASE_URLS.priority, {
  preserveAxiosErrors: true,
});
export const axiosNewApiServices = createAxiosInstance(API_BASE_URLS.newapi);

// server3 uses DefaultContractResolver which preserves C# PascalCase property names.
// This interceptor normalises every response to camelCase so the rest of the
// front-end code keeps working exactly as it did when newapi3 used CamelCase serialisation.
axiosNewApiServices.interceptors.response.use((response) => {
  if (response.data != null && typeof response.data === 'object') {
    response.data = toCamelCaseKeys(response.data);
  }
  return response;
});

export const axiosNewAppServices = createAxiosInstance(API_BASE_URLS.newapp);
export const axiosServerNodeServices = createAxiosInstance(API_BASE_URLS.servernode);
export const axiosDashboardServices = createAxiosInstance(API_BASE_URLS.dashboard);

export async function fetcher(
  method: FetchMethod,
  args: string | [string, AxiosRequestConfig],
  serviceType: fetchTypes,
  _legacyOption?: boolean,
) {
  try {
    const [url, config = {}] = Array.isArray(args) ? args : [args, {}];

    // Separate `data` and config
    const { data, ...restConfig } = config;

    const requestConfig: AxiosRequestConfig = {
      ...restConfig,
      ...(method === 'get' ? { params: data } : {}),
    };

    const serviceByType: { [key in fetchTypes]: AxiosInstance } = {
      app: axiosAppServices,
      history: axiosHistoryServices,
      messages: axiosMessagesServices,
      api: axiosApiServices,
      priority: axiosPriorityServices,
      newapi: axiosNewApiServices,
      newapp: axiosNewAppServices,
      servernode: axiosServerNodeServices,
      dashboard: axiosDashboardServices,
    };

    const service = serviceByType[serviceType];

    if (['post', 'put', 'patch'].includes(method)) {
      const res = await service[method](url, data, requestConfig);
      return res.data;
    }

    const res = await service[method](url, requestConfig);
    return res.data;
  } catch (error) {
    const [url] = Array.isArray(args) ? args : [args];
    /** 404 = נתיב לא קיים בסביבה — לא לזעזע את הקונסול כמו שגיאה קריטית */
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      if (import.meta.env.DEV) {
        console.debug('[fetcher] 404 (no body):', url);
      }
    } else {
      console.error(url + ' fetch error: ', error);
    }
    throw error;
  }
}
